import { FiArrowLeft } from 'react-icons/fi';
import Avatar from '../Shared/Avatar';
import searchIcon from '../../../assets/search_custom.svg';
import { useUserPresence } from '../../../context/TalkPresenceContext';
import { useI18n } from '../../../i18n/I18nContext';

// ... (rest of imports)

/**
 * ChatHeader Component
 * Displays conversation info, typing status, and action buttons
 *
 * @param {Object} props
 * @param {Object} props.conversation - Current conversation data
 * @param {Object} props.currentUser - Current logged-in user
 * @param {Array} props.typingUsers - Users currently typing
 * @param {function} props.onBack - Back button handler (mobile)
 * @param {function} props.onSearch - Search button handler
 * @param {function} props.onInfoClick - Conversation info click handler
 */
const ChatHeader = ({
  conversation,
  currentUser,
  typingUsers = [],
  onBack,
  onSearch,
  onInfoClick,
  onAddMember,
  onVideoCall,
  onVoiceCall,
  onShowHistory,
  hasCallHistory = false,
}) => {
  const { t, locale } = useI18n();
  const otherUser = conversation?.type === 'direct'
    ? conversation.users?.find(user => String(user.id) !== String(currentUser?.id)) : null;
  const presence = useUserPresence(otherUser?.id, otherUser?.last_seen_at);
  if (!conversation) {
    return null;
  }

  // Get conversation display info
  const getConversationInfo = () => {
    if (conversation.type === 'team' && conversation.context) {
      return {
        name: conversation.context.name || 'Team Chat',
        avatar: conversation.context.avatar,
        subtitle: `${conversation.users?.length || 0} participants`,
      };
    }

    if (conversation.type === 'group') {
      const otherUsers = conversation.users?.filter((u) => u.id !== currentUser?.id) || [];
      return {
        name: otherUsers.map(u => u.name).join(', ') || 'Group Chat',
        avatar: null, // Default avatar will be shown
        subtitle: `${conversation.users?.length || 0} participants`,
      };
    }

    // Direct conversation
    let subtitle = '';
    const timestamp = presence.last_seen_at || otherUser?.last_seen_at;
    const lastSeen = new Date(timestamp || NaN);
    if (presence.is_online) {
      subtitle = t('talk.presence.online', {}, 'Online');
    } else if (Number.isFinite(lastSeen.getTime())) {
      const now = new Date();
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);

      const isToday = lastSeen.toDateString() === now.toDateString();
      const isYesterday = lastSeen.toDateString() === yesterday.toDateString();

      const timeString = lastSeen.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
      });

      if (isToday) {
        subtitle = t('talk.presence.today', { time: timeString }, `Last seen at ${timeString}`);
      } else if (isYesterday) {
        subtitle = t('talk.presence.yesterday', { time: timeString }, `Last seen yesterday at ${timeString}`);
      } else {
        const date = lastSeen.toLocaleDateString(locale);
        subtitle = t('talk.presence.date', { date }, `Last seen on ${date}`);
      }
    } else {
      subtitle = ''; // Or 'Offline'
    }

    return {
      name: otherUser?.name || 'User',
      avatar: otherUser?.avatar,
      subtitle: subtitle,
    };
  };

  const getTypingText = () => {
    if (typingUsers.length === 0) return null;

    if (typingUsers.length === 1) {
      return conversation.type === 'team'
        ? `${typingUsers[0].name?.split(' ')[0]} is typing...`
        : 'Typing...';
    }

    if (typingUsers.length === 2) {
      return `${typingUsers[0].name?.split(' ')[0]} and ${typingUsers[1].name?.split(' ')[0]} are typing...`;
    }

    return 'Several people are typing...';
  };

  const info = getConversationInfo();
  const typingText = getTypingText();

  return (
    <div className="talk-chat-header">
      {onBack && (
        <button
          className="talk-icon-btn talk-back-btn"
          onClick={onBack}
          aria-label="Back to conversations"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 12H5M5 12L12 19M5 12L12 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      <div
        className="talk-chat-header-info"
        onClick={onInfoClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onInfoClick?.();
          }
        }}
      >
        <Avatar
          src={info.avatar}
          name={info.name}
          size={40}
          isOnline={Boolean(otherUser && presence.is_online)}
          status={otherUser && presence.is_online ? 'online' : 'offline'}
          clickable={false}
        />

        <div className="talk-chat-header-text">
          <h3 className="talk-chat-header-name">{info.name}</h3>
          <p className="talk-chat-header-status">
            {typingText || info.subtitle}
          </p>
        </div>
      </div>

      <div className="talk-chat-header-actions">
        {(conversation.type === 'direct' || conversation.type === 'group') && onAddMember && (
          <button
            className="talk-header-add-member-btn"
            onClick={onAddMember}
            aria-label="Add Member"
            title="Add Member"
            id="talk-add-member-btn"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.75 3.375C7.297 3.375 7.82175 3.5923 8.20854 3.97909C8.59533 4.36587 8.8125 4.89063 8.8125 5.43763C8.8125 5.98462 8.59533 6.50938 8.20854 6.89617C7.82175 7.28296 7.297 7.50025 6.75 7.50025C6.20301 7.50025 5.67825 7.28296 5.29146 6.89617C4.90467 6.50938 4.6875 5.98462 4.6875 5.43763C4.6875 4.89063 4.90467 4.36587 5.29146 3.97909C5.67825 3.5923 6.20301 3.375 6.75 3.375ZM6.75 2.25C5.90462 2.25 5.09388 2.58583 4.49608 3.18363C3.89828 3.78143 3.5625 4.59218 3.5625 5.43763C3.5625 6.28307 3.89828 7.09382 4.49608 7.69162C5.09388 8.28943 5.90462 8.62525 6.75 8.62525C7.59538 8.62525 8.40612 8.28943 9.00392 7.69162C9.60172 7.09382 9.9375 6.28307 9.9375 5.43763C9.9375 4.59218 9.60172 3.78143 9.00392 3.18363C8.40612 2.58583 7.59538 2.25 6.75 2.25ZM11.4559 13.5005V13.8875C11.4559 13.987 11.4164 14.0823 11.3461 14.1527C11.2757 14.223 11.1804 14.2625 11.0809 14.2625H2.41912C2.31966 14.2625 2.2243 14.223 2.15392 14.1527C2.08355 14.0823 2.04412 13.987 2.04412 13.8875V13.5005C2.04412 12.6324 2.52296 11.821 3.28421 11.4019C4.34737 10.8166 5.53982 10.5009 6.75 10.5005C7.96024 10.5009 9.1527 10.8166 10.2158 11.4019C10.977 11.821 11.4559 12.6324 11.4559 13.5005ZM12.5809 13.5005C12.5809 12.288 11.916 11.1578 10.8529 10.5731C9.60336 9.88582 8.1878 9.50856 6.75 9.3755C5.31219 9.50856 3.89664 9.88582 2.64712 10.5731C1.58396 11.1578 0.919125 12.288 0.919125 13.5005V13.8875C0.919125 14.2853 1.07716 14.6669 1.35846 14.9482C1.63977 15.2295 2.0213 15.3875 2.41912 15.3875H11.0809C11.4787 15.3875 11.8602 15.2295 12.1415 14.9482C12.4228 14.6669 12.5809 14.2853 12.5809 13.8875V13.5005Z" fill="#1C1B1F"/>
              <path d="M17.9998 9.56336C17.9998 9.71254 17.9406 9.85562 17.8351 9.96111C17.7296 10.0666 17.5865 10.1259 17.4373 10.1259H16.3123V11.2509C16.3123 11.4 16.2531 11.5431 16.1476 11.6486C16.0421 11.7541 15.899 11.8123 15.75 11.8123C15.601 11.8123 15.4579 11.7541 15.3524 11.6486C15.2469 11.5431 15.1876 11.4 15.1876 11.2509V10.1259H14.0626C13.9135 10.1259 13.7704 10.0666 13.6649 9.96111C13.5594 9.85562 13.5002 9.71254 13.5002 9.56336C13.5002 9.41419 13.5594 9.27111 13.6649 9.16562C13.7704 9.06013 13.9135 9.00086 14.0626 9.00086H15.1876V7.87586C15.1876 7.72669 15.2469 7.58361 15.3524 7.47812C15.4579 7.37263 15.601 7.31336 15.75 7.31336C15.899 7.31336 16.0421 7.37263 16.1476 7.47812C16.2531 7.58361 16.3123 7.72669 16.3123 7.87586V9.00086H17.4373C17.5865 9.00086 17.7296 9.06013 17.8351 9.16562C17.9406 9.27111 17.9998 9.41419 17.9998 9.56336Z" fill="#1C1B1F"/>
            </svg>
            <span>Add Member</span>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 11.25L5.25 7.5H12.75L9 11.25Z" fill="#49454F"/>
            </svg>
          </button>
        )}

        {/* Show History button — only when past calls exist */}
        {hasCallHistory && onShowHistory && (
          <button
            className="talk-header-history-btn"
            onClick={onShowHistory}
            aria-label="Show call history"
            title="Show call history"
            id="talk-show-history-btn"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M9.56292 5.6255V8.68198L12.1026 10.2057C12.2305 10.2825 12.3227 10.407 12.3588 10.5518C12.395 10.6966 12.3721 10.8498 12.2953 10.9777C12.2184 11.1056 12.0939 11.1978 11.9491 11.2339C11.8044 11.27 11.6512 11.2472 11.5232 11.1703L8.71073 9.48284C8.62749 9.43283 8.55862 9.36212 8.51081 9.2776C8.463 9.19307 8.43789 9.09761 8.43792 9.0005V5.6255C8.43792 5.47631 8.49718 5.33324 8.60267 5.22775C8.70816 5.12226 8.85124 5.063 9.00042 5.063C9.1496 5.063 9.29268 5.12226 9.39817 5.22775C9.50366 5.33324 9.56292 5.47631 9.56292 5.6255ZM15.7504 3.938C15.6012 3.938 15.4582 3.99726 15.3527 4.10275C15.2472 4.20824 15.1879 4.35131 15.1879 4.5005V5.76612C14.7414 5.24862 14.2858 4.75011 13.7732 4.2312C12.8352 3.29302 11.6414 2.65209 10.3412 2.38849C9.0409 2.12488 7.6918 2.2503 6.46241 2.74906C5.23303 3.24782 4.17788 4.09781 3.42881 5.19282C2.67975 6.28784 2.26998 7.57931 2.25071 8.90588C2.23144 10.2324 2.60353 11.5353 3.32048 12.6516C4.03743 13.7679 5.06744 14.6482 6.28183 15.1824C7.49621 15.7167 8.8411 15.8812 10.1485 15.6555C11.4558 15.4298 12.6677 14.8238 13.6326 13.9132C13.6863 13.8624 13.7296 13.8016 13.7598 13.7341C13.79 13.6666 13.8066 13.5938 13.8087 13.5199C13.8108 13.446 13.7983 13.3724 13.7719 13.3033C13.7456 13.2342 13.7059 13.171 13.6551 13.1173C13.6043 13.0636 13.5435 13.0203 13.476 12.9901C13.4085 12.9599 13.3357 12.9433 13.2618 12.9412C13.1879 12.9391 13.1143 12.9516 13.0452 12.978C12.9761 13.0043 12.9129 13.044 12.8592 13.0948C12.0548 13.8528 11.045 14.3571 9.95577 14.5446C8.86655 14.7322 7.74622 14.5947 6.73468 14.1493C5.72314 13.7039 4.86521 12.9704 4.26805 12.0404C3.67088 11.1104 3.36093 10.025 3.37691 8.91988C3.39288 7.81475 3.73407 6.73881 4.35788 5.82643C4.98168 4.91405 5.86045 4.20566 6.88444 3.78972C7.90843 3.37377 9.03227 3.26872 10.1156 3.48766C11.199 3.70661 12.1938 4.23985 12.9759 5.02081C13.5482 5.60018 14.0496 6.15847 14.5551 6.7505H12.9379C12.7887 6.7505 12.6457 6.80976 12.5402 6.91525C12.4347 7.02074 12.3754 7.16381 12.3754 7.313C12.3754 7.46218 12.4347 7.60526 12.5402 7.71074C12.6457 7.81623 12.7887 7.8755 12.9379 7.8755H15.7504C15.8996 7.8755 16.0427 7.81623 16.1482 7.71074C16.2537 7.60526 16.3129 7.46218 16.3129 7.313V4.5005C16.3129 4.35131 16.2537 4.20824 16.1482 4.10275C16.0427 3.99726 15.8996 3.938 15.7504 3.938Z"
                fill="#020202"
              />
            </svg>
            Show History
          </button>
        )}

        <button
          className="talk-header-action-btn"
          onClick={onVoiceCall}
          aria-label="Voice call"
          title="Voice call"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.8472 14.8554L16.4306 12.8764L16.4184 12.8707C16.1892 12.7727 15.939 12.7333 15.6907 12.7562C15.4424 12.7792 15.2037 12.8636 14.9963 13.002C14.9718 13.0181 14.9484 13.0357 14.9259 13.0545L12.6441 14.9998C11.1984 14.2976 9.70595 12.8164 9.00376 11.3895L10.9519 9.07294C10.9706 9.0495 10.9884 9.02606 11.0053 9.00075C11.1407 8.79384 11.2229 8.55667 11.2445 8.31035C11.2661 8.06402 11.2264 7.81618 11.1291 7.58887V7.57762L9.14438 3.15356C9.0157 2.85662 8.79444 2.60926 8.51362 2.44841C8.2328 2.28756 7.9075 2.22184 7.58626 2.26106C6.31592 2.42822 5.14986 3.05209 4.30588 4.01615C3.4619 4.98021 2.99771 6.21852 3.00001 7.49981C3.00001 14.9436 9.05626 20.9998 16.5 20.9998C17.7813 21.0021 19.0196 20.5379 19.9837 19.6939C20.9477 18.85 21.5716 17.6839 21.7388 16.4136C21.7781 16.0924 21.7125 15.7672 21.5518 15.4864C21.3911 15.2056 21.144 14.9843 20.8472 14.8554ZM16.5 19.4998C13.3185 19.4963 10.2682 18.2309 8.01856 15.9813C5.76888 13.7316 4.50348 10.6813 4.50001 7.49981C4.49648 6.58433 4.82631 5.69887 5.42789 5.00879C6.02947 4.3187 6.86167 3.87118 7.76907 3.74981C7.7687 3.75355 7.7687 3.75732 7.76907 3.76106L9.73782 8.16731L7.80001 10.4867C7.78034 10.5093 7.76247 10.5335 7.74657 10.5589C7.60549 10.7754 7.52273 11.0246 7.5063 11.2825C7.48988 11.5404 7.54035 11.7981 7.65282 12.0307C8.5022 13.7679 10.2525 15.5051 12.0084 16.3536C12.2428 16.465 12.502 16.5137 12.7608 16.495C13.0196 16.4762 13.2692 16.3907 13.485 16.2467C13.5091 16.2305 13.5322 16.2129 13.5544 16.1942L15.8334 14.2498L20.2397 16.2232C20.2397 16.2232 20.2472 16.2232 20.25 16.2232C20.1301 17.1319 19.6833 17.9658 18.9931 18.5689C18.3028 19.172 17.4166 19.5029 16.5 19.4998Z" fill="currentColor"/>
          </svg>
        </button>

        <button
          className="talk-header-action-btn"
          onClick={onVideoCall}
          aria-label="Video call"
          title="Video call"
        >
          <svg
            className="talk-header-video-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M23.6034 6.84375C23.4838 6.77964 23.3491 6.74919 23.2135 6.75563C23.078 6.76207 22.9467 6.80516 22.8337 6.88031L19.5 9.09844V6.75C19.5 6.35218 19.342 5.97064 19.0607 5.68934C18.7794 5.40804 18.3978 5.25 18 5.25H3C2.60218 5.25 2.22064 5.40804 1.93934 5.68934C1.65804 5.97064 1.5 6.35218 1.5 6.75V17.25C1.5 17.6478 1.65804 18.0294 1.93934 18.3107C2.22064 18.592 2.60218 18.75 3 18.75H18C18.3978 18.75 18.7794 18.592 19.0607 18.3107C19.342 18.0294 19.5 17.6478 19.5 17.25V14.9062L22.8337 17.1291C22.9576 17.2095 23.1024 17.2515 23.25 17.25C23.4489 17.25 23.6397 17.171 23.7803 17.0303C23.921 16.8897 24 16.6989 24 16.5V7.5C23.9991 7.36506 23.9617 7.23287 23.8919 7.11737C23.8221 7.00187 23.7225 6.90734 23.6034 6.84375ZM18 17.25H3V6.75H18V17.25ZM22.5 15.0984L19.5 13.0988V10.9012L22.5 8.90625V15.0984Z"
              fill="currentColor"
            />
          </svg>
        </button>

        <button
          className="talk-header-search-btn"
          onClick={onSearch}
          aria-label="Search in conversation"
          title="Search messages"
        >
          <img src={searchIcon} alt="search" width={22} height={22} />
        </button>
      </div>
    </div>
  );
};

export default ChatHeader;
